import { usePlatformPrivilege } from '@mews-commander/core';
import { useRepositoryQuery } from '@mews-commander/data-access';
import { TextKey } from '@mews-commander/enums';
import { LocalizedParagraph, LocalizedTypography } from '@mews-commander/ui';
import { LocalizedText, lt, useDialog } from '@mews/framework';
import { Button, IconName, Stack, StackItem, Tag } from '@optimus-web/core';
import { ContactUsButton } from '../components/ContactUsButton';
import { SecurityCard } from '../components/SecurityCard';
import { StatusTag } from '../components/StatusTag';
import { useOwnerIdQueryPayload } from '../utils/useOwnerId';
import { SecurityDataTestId } from '../utils/SecurityDataTestId';
import { ExceptionsDialog } from './ExceptionsDialog';

export const Authentication = () => {
    const [{ active, onClose }, toggleDialog] = useDialog();
    const ownerIdPayload = useOwnerIdQueryPayload();
    const { data, isLoading } = useRepositoryQuery('mfaConfiguration', 'get', ownerIdPayload);

    const canAccessAllEnterprises = usePlatformPrivilege('accessAllEnterprises');
    const isPlatformAdmin = usePlatformPrivilege('admin');

    const isPrivilegedPlatformUser = canAccessAllEnterprises || isPlatformAdmin;
    const hasMfaEnforced = !!data?.enforceAuthenticatorSetup;

    return (
        <>
            <SecurityCard
                icon={IconName.Security}
                isLoading={isLoading}
                variant={hasMfaEnforced ? 'default' : 'subtle'}
                data-test-id={SecurityDataTestId.MfaCard}
                action={
                    hasMfaEnforced || isPrivilegedPlatformUser ? (
                        <Button
                            variant={hasMfaEnforced ? 'tertiary' : 'secondary'}
                            onClick={toggleDialog}
                            size="small"
                            type="button"
                        >
                            <LocalizedText textKey={hasMfaEnforced ? TextKey.Manage : TextKey.Configure} />
                        </Button>
                    ) : (
                        <ContactUsButton />
                    )
                }
            >
                <Stack spacing="100" itemAlignment="center">
                    <LocalizedTypography
                        textKey={TextKey.TwoFactorAuthenticationCardTitle}
                        textStyle="titleSmallStrong"
                        as="div"
                    />
                    <StatusTag isEnabled={hasMfaEnforced} />
                </Stack>

                <LocalizedParagraph
                    align="left"
                    textKey={TextKey.TwoFactorAuthenticationCardDescription}
                    variant="secondary"
                />
                {data?.exemptEnterpriseProfiles?.length ? (
                    <StackItem>
                        <Tag variant="warning" leadingIcon={IconName.Profile} outline>
                            {lt('TwoFactorAuthenticationCardExceptions', {
                                count: data.exemptEnterpriseProfiles.length,
                            })}
                        </Tag>
                    </StackItem>
                ) : null}
            </SecurityCard>
            {data ? <ExceptionsDialog active={active} onClose={onClose} data={data} /> : null}
        </>
    );
};
